import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Stack,
    Chip,
    Card,
    Avatar,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowForward, BusinessCenter, PlayArrow } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { Parallax } from 'react-scroll-parallax';
import { getTheme } from '../../theme/theme';
import heroImage from '../../assets/images/solutions.jpg';
const SolutionsHero = ({ mode }) => {
    const theme = getTheme(mode);
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                delayChildren: 0.2,
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }
        }
    };

    const statsVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: { delay: 0.6, duration: 0.5 }
        }
    };

    return (
        <Box
            id="home"
            sx={{
                position: 'relative',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(13, 13, 34, 0.95) 0%, rgba(26, 26, 56, 0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(250, 252, 255, 0.97) 0%, rgba(240, 245, 255, 0.97) 100%)',
                overflow: 'hidden',
                pt: { xs: 10, md: 0 },
                pb: { xs: 8, md: 0 },
            }}
        >
            {/* Animated background elements */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
                {/* Glass orbs / blobs */}
                <Box
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.7 }}
                    transition={{ duration: 1.5 }}
                    sx={{
                        position: 'absolute',
                        width: { xs: 300, md: 500 },
                        height: { xs: 300, md: 500 },
                        borderRadius: '50%',
                        background: theme.palette.mode === 'dark'
                          ? 'radial-gradient(circle, rgba(106, 27, 255, 0.15), rgba(106, 27, 255, 0.05))'
                          : 'radial-gradient(circle, rgba(94, 0, 255, 0.08), rgba(94, 0, 255, 0.02))',
                        filter: 'blur(40px)',
                        top: { xs: -100, md: -150 },
                        right: { xs: -100, md: -100 },
                    }}
                />
                <Box
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.6 }}
                    transition={{ duration: 1.5, delay: 0.3 }}
                    sx={{
                        position: 'absolute',
                        width: { xs: 250, md: 400 },
                        height: { xs: 250, md: 400 },
                        borderRadius: '50%',
                        background: theme.palette.mode === 'dark'
                            ? 'radial-gradient(circle, rgba(0, 174, 239, 0.1), rgba(0, 174, 239, 0.03))'
                            : 'radial-gradient(circle, rgba(0, 106, 255, 0.07), rgba(0, 106, 255, 0.02))',
                        filter: 'blur(40px)',
                        bottom: { xs: -100, md: -100 },
                        left: { xs: -100, md: -100 },
                    }}
                />

                {/* Grid pattern overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `
              linear-gradient(${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(94, 0, 255, 0.015)'} 1px, transparent 1px),
              linear-gradient(90deg, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(94, 0, 255, 0.015)'} 1px, transparent 1px)
            `,
                        backgroundSize: '40px 40px',
                        zIndex: 1,
                    }}
                />
            </Box>

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                    {/* Left Content Column */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate={isVisible ? "visible" : "hidden"}
                        >
                            {/* Badge */}
                            <motion.div variants={itemVariants}>
                                <Chip
                                    label="OUR SOLUTIONS"
                                    color="primary"
                                    size={isMobile ? "small" : "medium"}
                                    sx={{
                                        mb: 3,
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        px: 2,
                                        py: 0.75,
                                        backdropFilter: 'blur(8px)',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(106, 27, 255, 0.15)'
                                            : 'rgba(94, 0, 255, 0.08)',
                                        border: '1px solid',
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(106, 27, 255, 0.3)'
                                            : 'rgba(94, 0, 255, 0.15)',
                                    }}
                                />
                            </motion.div>

                            {/* Headline */}
                            <motion.div variants={itemVariants}>
                                <Typography
                                    variant={isMobile ? 'h3' : isTablet ? 'h2' : 'h1'}
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        mb: 2,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        lineHeight: 1.1,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Cloud innovation that leads, disrupts, and delivers
                                </Typography>
                            </motion.div>

                            {/* Subheading */}
                            <motion.div variants={itemVariants}>
                                <Typography
                                    variant={isMobile ? 'body1' : 'h6'}
                                    sx={{
                                        mb: 4,
                                        color: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.7)'
                                            : 'rgba(30, 40, 50, 0.7)',
                                        maxWidth: '90%',
                                        lineHeight: 1.5,
                                    }}
                                >
                                   Leading the way with cloud innovation that disrupts the norm. Delivering impactful solutions that drive real change.
                                </Typography>
                            </motion.div>


                        </motion.div>
                    </Grid>

                    {/* Right Image Column */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ position: 'relative' }}>
                            {/* Main Image */}
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                            >
                                <Parallax speed={-5}>
                                    <Box
                                        component="img"
                                        src={heroImage} // Replace with your actual image path
                                        alt="Digital Platform Dashboard"
                                        sx={{
                                            width: '80%',
                                            height: 'auto',
                                            borderRadius: '20px',
                                            boxShadow: theme.palette.mode === 'dark'
                                                ? '0 20px 40px rgba(0, 0, 0, 0.3)'
                                                : '0 20px 40px rgba(0, 0, 0, 0.1)',
                                            border: theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255, 255, 255, 0.1)'
                                                : '1px solid rgba(0, 0, 0, 0.05)',
                                            transition: 'transform 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                            },
                                        }}
                                    />
                                </Parallax>
                            </motion.div>

                            {/* Floating Stats Card */}
                            {!isMobile && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.8, duration: 0.5 }}
                                    style={{
                                        position: 'absolute',
                                        bottom: '10%',
                                        left: '0%',
                                        zIndex: 2,
                                    }}
                                >
                                    <motion.div
                                        animate={{
                                            y: [0, -10, 0],
                                        }}
                                        transition={{
                                            duration: 4,
                                            repeat: Infinity,
                                            repeatType: "reverse",
                                            ease: "easeInOut",
                                        }}
                                    >
                                        <Card
                                            sx={{
                                                p: 2.5,
                                                borderRadius: '16px',
                                                backdropFilter: 'blur(16px)',
                                                background: theme.palette.mode === 'dark'
                                                    ? 'rgba(26, 26, 46, 0.8)'
                                                    : 'rgba(255, 255, 255, 0.9)',
                                                boxShadow: theme.palette.mode === 'dark'
                                                    ? '0 15px 35px rgba(0, 0, 0, 0.4)'
                                                    : '0 15px 35px rgba(94, 0, 255, 0.1)',
                                                border: '1px solid',
                                                borderColor: theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                    : 'rgba(94, 0, 255, 0.1)',
                                            }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar
                                                    sx={{
                                                        bgcolor: theme.palette.mode === 'dark'
                                                            ? 'rgba(106, 27, 255, 0.2)'
                                                            : 'rgba(94, 0, 255, 0.1)',
                                                        width: 48,
                                                        height: 48,
                                                    }}
                                                >
                                                    <BusinessCenter color="primary" />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                        <CountUp
                                                            end={98}
                                                            suffix="%"
                                                            duration={2.5}
                                                            enableScrollSpy
                                                        />
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Client satisfaction rate
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </motion.div>
                                </motion.div>
                            )}

                            {/* Floating Technologies Card */}
                            {!isMobile && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 1, duration: 0.5 }}
                                    style={{
                                        position: 'absolute',
                                        top: '5%',
                                        right: '10%',
                                        zIndex: 2,
                                    }}
                                >
                                    <motion.div
                                        animate={{
                                            y: [0, -8, 0],
                                        }}
                                        transition={{
                                            duration: 4.5,
                                            delay: 0.5,
                                            repeat: Infinity,
                                            repeatType: "reverse",
                                            ease: "easeInOut",
                                        }}
                                    >
                                        <Card
                                            sx={{
                                                p: 2,
                                                borderRadius: '16px',
                                                width: '200px',
                                                backdropFilter: 'blur(16px)',
                                                background: theme.palette.mode === 'dark'
                                                    ? 'rgba(26, 26, 46, 0.8)'
                                                    : 'rgba(255, 255, 255, 0.9)',
                                                boxShadow: theme.palette.mode === 'dark'
                                                    ? '0 15px 35px rgba(0, 0, 0, 0.4)'
                                                    : '0 15px 35px rgba(94, 0, 255, 0.1)',
                                                border: '1px solid',
                                                borderColor: theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.1)'
                                                    : 'rgba(94, 0, 255, 0.1)',
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                Solutions We have Delivered
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                                {['ManuPro', 'Custom ERP Solutions', 'PrintPro'].map((tech) => (
                                                    <Chip
                                                        key={tech}
                                                        label={tech}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: theme.palette.mode === 'dark'
                                                                ? 'rgba(106, 27, 255, 0.1)'
                                                                : 'rgba(94, 0, 255, 0.05)',
                                                            borderColor: theme.palette.mode === 'dark'
                                                                ? 'rgba(106, 27, 255, 0.3)'
                                                                : 'rgba(94, 0, 255, 0.1)',
                                                            color: theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.main,
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Card>
                                    </motion.div>
                                </motion.div>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {/* Stats bar */}
                {/* {!isMobile && (
          <motion.div
            variants={statsVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
          >
            <Box
              sx={{
                mt: 8,
                py: 3,
                px: 4,
                display: 'flex',
                justifyContent: 'space-between',
                borderRadius: '20px',
                backdropFilter: 'blur(16px)',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(26, 26, 46, 0.7)'
                  : 'rgba(255, 255, 255, 0.7)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 10px 30px rgba(0, 0, 0, 0.2)'
                  : '0 10px 30px rgba(94, 0, 255, 0.07)',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(94, 0, 255, 0.05)',
              }}
            >
              {[
                { label: 'Projects Delivered', value: 5, suffix: '+' },
                { label: 'Team Members', value: 10, suffix: '+' },
                { label: 'Client Retention', value: 95, suffix: '%' },
                { label: 'Years Experience', value: 8, suffix: '+' }
              ].map((stat, index) => (
                <Box key={index} sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 800,
                      mb: 0.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    <CountUp
                      end={stat.value}
                      suffix={stat.suffix}
                      duration={2.5}
                      enableScrollSpy
                    />
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        )} */}
            </Container>
        </Box>
    );
};

export default SolutionsHero